import React, { useEffect, useRef, useState } from 'react';
import { Card } from './card';
import { Avatar, AvatarFallback, AvatarImage } from './avatar';

interface User {
  userId: string;
  _id?: string; // Add _id for compatibility
  profilePic?: string;
  name: string;
  order?: number;
  firstName?: string;
  lastName?: string;
  email?: string;
  role?: string;
  address?: string;
  primarySkills?: string[];
  secondarySkills?: string[];
  experience?: string;
  designation?: string;
  company?: string;
  // Additional fields for aspirants
  degree?: string;
  fieldOfStudy?: string;
}

interface CarouselSettings {
  speed: number;
  direction: 'left' | 'right';
  focusCardSize: number;
  normalCardSize: number;
}

interface HighlightsCarouselProps {
  users: User[];
  settings: CarouselSettings;
  title: string;
  className?: string;
}

export const HighlightsCarousel: React.FC<HighlightsCarouselProps> = ({
  users,
  settings,
  title,
  className = ''
}) => {
  console.log('HighlightsCarousel: Rendering with props:', { users: users?.length, settings, title });
  
  // Provide default settings if settings is undefined
  const safeSettings = settings || {
    speed: 50,
    direction: 'right' as const,
    focusCardSize: 120,
    normalCardSize: 80
  };
  const containerRef = useRef<HTMLDivElement>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);

  // Duplicate users for seamless scrolling
  const duplicatedUsers = [...users, ...users, ...users];

  useEffect(() => {
    if (!containerRef.current || users.length === 0) return;

    const container = containerRef.current;
    const scrollWidth = container.scrollWidth;
    const clientWidth = container.clientWidth;
    

    
    if (scrollWidth <= clientWidth) return;

    let animationId: number;
    let currentScroll = 0;
    const maxScroll = scrollWidth - clientWidth;

    const animate = () => {
      if (isHovered) {
        animationId = requestAnimationFrame(animate);
        return;
      }

      if (safeSettings.direction === 'right') {
        currentScroll += safeSettings.speed / 60; // Convert to per-frame speed
        if (currentScroll >= maxScroll) {
          currentScroll = 0;
        }
      } else {
        currentScroll -= safeSettings.speed / 60;
        if (currentScroll <= 0) {
          currentScroll = maxScroll;
        }
      }

      container.scrollLeft = currentScroll;
      animationId = requestAnimationFrame(animate);
    };

    animationId = requestAnimationFrame(animate);

    return () => {
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
    };
  }, [users, settings, isHovered]);

  // Handle scroll to detect focused card
  const handleScroll = () => {
    if (!containerRef.current) return;

    const container = containerRef.current;
    const scrollLeft = container.scrollLeft;
    const containerWidth = container.clientWidth;
    const cardWidth = 192 + 16 + 16; // card width (w-48) + gap (gap-4) + padding
    const centerPosition = scrollLeft + containerWidth / 2;
    
    const index = Math.floor(centerPosition / cardWidth) % users.length;
    setFocusedIndex(index);
  };

  const getInitials = (firstName: string, lastName: string) => {
    if (!firstName && !lastName) return '?';
    if (!lastName) {
      // If only firstName is provided, take first two characters
      return firstName.substring(0, 2).toUpperCase();
    }
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  const getProfileImageUrl = (user: User) => {
    return user.profilePic || undefined;
  };

  console.log('HighlightsCarousel: Users array:', users);
  console.log('HighlightsCarousel: Users length:', users?.length);
  
  if (!users || users.length === 0) {
    console.log('HighlightsCarousel: No users, showing empty state');
    return (
      <div className={`w-full py-8 ${className}`}>
        <h3 className="text-xl font-semibold text-gray-700 mb-4">{title}</h3>
        <div className="text-center text-gray-500 py-8">
          No users selected for highlights
        </div>
      </div>
    );
  }

  return (
    <div className={`w-full py-8 ${className}`}>
      <div
        ref={containerRef}
        className="flex gap-4 overflow-x-hidden hide-scrollbar items-center min-h-80 relative"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onScroll={handleScroll}
        style={{
          scrollBehavior: isHovered ? 'smooth' : 'auto'
        }}
      >
        {/* Title positioned on the left side, vertically centered - sticky position */}
        {title && (
          <div className="sticky left-0 top-1/2 transform -translate-y-1/2 z-20 bg-orange-500/80 backdrop-blur-sm px-4 py-2 rounded-lg shadow-sm">
            <h3 className="text-xl font-semibold text-white whitespace-nowrap">{title}</h3>
          </div>
        )}
        
        {/* Spacer to prevent cards from overlapping with title */}
        {title && <div className="flex-shrink-0 w-32"></div>}
        {duplicatedUsers.filter(user => user).map((user, index) => {
          console.log('HighlightsCarousel: Rendering user:', user);
          
          // Debug: Log professional information fields
          if (index === 0) {
            console.log('🔍 Sample user professional data:', {
              name: user.name,
              designation: user.designation,
              company: user.company,
              experience: user.experience,
              address: user.address,
              primarySkills: user.primarySkills,
              degree: user.degree,
              fieldOfStudy: user.fieldOfStudy
            });
          }
          
          // Skip rendering if user data is incomplete
          if (!user || !user.name) {
            console.log('HighlightsCarousel: Skipping incomplete user:', user);
            return null;
          }
          
          const originalIndex = index % users.length;
          const isFocused = focusedIndex === originalIndex;
          const scale = isFocused ? safeSettings.focusCardSize / 100 : safeSettings.normalCardSize / 100;
          
          return (
            <Card
              key={`${user.userId || user._id}-${index}`}
              className="flex-shrink-0 w-48 p-6 transition-all duration-300 ease-in-out min-h-72"
              style={{
                transform: `scale(${scale})`,
                zIndex: isFocused ? 10 : 1
              }}
            >
              <div className="flex flex-col items-center text-center space-y-3">
                <Avatar className="w-20 h-20 flex-shrink-0">
                  <AvatarImage 
                    src={getProfileImageUrl(user)} 
                    alt={user.name}
                    className="object-cover"
                  />
                  <AvatarFallback className="bg-primary text-white text-lg font-semibold">
                    {getInitials(user.name, '')}
                  </AvatarFallback>
                </Avatar>
                
                <div className="space-y-2 text-center">
                  <h4 className="font-semibold text-gray-900 text-base">
                    {user.name}
                  </h4>
                  
                  {/* Different content for Aspirants vs Mentors */}
                  {title === 'Aspirants' ? (
                    /* Aspirants Information - Only Degree and Field of Study */
                    <div className="space-y-1">
                      {/* Degree */}
                      {user.degree && (
                        <p className="text-sm text-gray-700 font-medium">
                          {user.degree}
                        </p>
                      )}
                      
                      {/* Field of Study */}
                      {user.fieldOfStudy && (
                        <p className="text-xs text-gray-600">
                          {user.fieldOfStudy}
                        </p>
                      )}
                    </div>
                  ) : (
                    /* Mentors Professional Information - Only Professional Details */
                    <div className="space-y-1">
                      {/* Current Position */}
                      {user.designation && (
                        <p className="text-sm text-gray-700 font-medium">
                          {user.designation}
                        </p>
                      )}
                      
                      {/* Current Company */}
                      {user.company && (
                        <p className="text-xs text-gray-600">
                          {user.company}
                        </p>
                      )}
                      
                      {/* Years of Experience */}
                      {user.experience && (
                        <p className="text-xs text-gray-500">
                          {user.experience} years experience
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
};
